import { put, take, fork, all, select, call } from "redux-saga/effects";
import { ContactServiceScope, ContactServiceAction } from "./types";
import {
  enableScopeLoading,
  disableScopeLoading,
} from "../scope-loading/actions";
import * as Api from "../../service/api";
import { ScopeErrorAction } from "../scope-error/types";
import RouteNames from "../../routes/type";
import { navigateTo } from "../../helper";
import { clearCookie, getFolderName } from "./helpers";
import { cleanUpstate, updateCSState } from "./actions";
import { RootState } from "../root-reducer";
import axios from "axios";

function* createDeskContactService() {
  while (true) {
    yield take(ContactServiceAction.CREATE_DESK_TICKET);
    yield put(enableScopeLoading(ContactServiceScope));
    try {
      const stateData = yield select(
        (state: RootState) => state.contactService
      );
      const claimOptions = yield select(
        (state: RootState) => state.user.claimOptions
      );

      const getLabel = (id, options = []) => {
        return options.find((opt: any) => opt.id == id)?.name || id;
      };

      const getSpecificDamageLabel = (id, options = []) => {
        return options.find((opt: any) => opt.id == id)?.name || id;
      };

      const documents = Object.entries(stateData.documents || {}).reduce(
        (acc: Record<string, any>, [key, val]: [string, any]) => {
          acc[key] = Array.isArray(val)
            ? val.map((u: string) => u.replace(/\.heic$/i, ".png"))
            : typeof val === "string"
            ? val.replace(/\.heic$/i, ".png")
            : val;
          return acc;
        },
        {}
      );

      const mappedData = {
        ...stateData,
        documents,
        damageDetails: stateData.claimItemDamage?.map((item) => ({
          damageCategory: getLabel(
            item.damage_category,
            claimOptions.damageTypes
          ),
          specificDamage: getSpecificDamageLabel(
            item.specific_damage,
            claimOptions.specificDamages
          ),
          damageArea: getLabel(
            item.damage_area,
            claimOptions.specificLocations
          ),
          howDidOccur: getLabel(
            item.how_did_it_happen,
            claimOptions.howDidOccur
          ),
          actionTaken: getLabel(
            item.did_you_take_any_steps_to_remedy,
            claimOptions.actionsTaken
          ),
          damageDate: item.when_did_you_notice_the_damage,
          notes: item.note,
        })),
      };

      const data = yield call(Api.deskTicket.contactServiceTicket, mappedData);
      if (data?.id) {
        clearCookie("data");
        clearCookie("foldername");
        yield put(cleanUpstate());
        yield navigateTo(
          `/${RouteNames.CONTACT_SERVICE}?step=5&ticket=${data?.ticketNumber}&id=${data?.ticketNumber}`
        );
      }
    } catch (error) {
      console.log("error:", error);
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: ContactServiceScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(ContactServiceScope));
    }
  }
}

function* uploadImageFlow() {
  while (true) {
    const { payload } = yield take(ContactServiceAction.UPLOAD_IMAGE);
    const { fieldName, files } = payload;
    yield put(enableScopeLoading(ContactServiceScope));
    try {
      const folderName = getFolderName();
      const uploadedFileUrls = yield all(
        files.map(function* (file) {
          const ext = file.name
            .substring(file.name.lastIndexOf(".") + 1)
            .toLowerCase();
          const fileType =
            ext === "heic" ? "image/heic" : file.type || "image/jpeg";
          const fileExtension = ext || fileType.split("/")[1];
          const { url } = yield Api.deskTicket.getPreSignedUrl({
            fileExtension,
            fileType,
            reqId: folderName,
          });
          yield axios.put(url, file, {
            headers: {
              "Content-Type": fileType,
            },
          });
          const data = new URL(url);
          data.search = "";
          return data.toString();
        })
      );
      yield put(
        updateCSState({ documents: { [fieldName]: uploadedFileUrls } })
      );
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: ContactServiceScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(ContactServiceScope));
    }
  }
}

export default function* contactServiceSaga() {
  yield fork(createDeskContactService);
  yield fork(uploadImageFlow);
}
